import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  auth,
  googleProvider,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  type User,
} from '@/lib/firebase';
import { GoogleAuthProvider } from 'firebase/auth';
import { getProfile, createProfile, isSupabaseConfigured } from '@traction/api-client';
import type { Profile } from '@traction/shared';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  gmailAccessToken: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [gmailAccessToken, setGmailAccessToken] = useState<string | null>(() => {
    // Try to restore from sessionStorage
    return sessionStorage.getItem('gmail_access_token');
  });

  async function loadProfile(uid: string, displayName?: string | null, photoURL?: string | null) {
    if (!isSupabaseConfigured()) {
      return;
    }

    try {
      const existingProfile = await getProfile(uid);
      if (existingProfile) {
        setProfile(existingProfile);
      } else if (displayName !== undefined) {
        // Create profile for new Google OAuth users
        const newProfile = await createProfile(uid, {
          full_name: displayName ?? '',
          photo_url: photoURL,
        });
        setProfile(newProfile);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  }

  useEffect(() => {
    // Handle Google OAuth redirect result
    getRedirectResult(auth)
      .then(async (result) => {
        if (result?.user) {
          // Get Gmail access token from OAuth credential
          const credential = GoogleAuthProvider.credentialFromResult(result);
          if (credential?.accessToken) {
            setGmailAccessToken(credential.accessToken);
            sessionStorage.setItem('gmail_access_token', credential.accessToken);
          }

          await loadProfile(
            result.user.uid,
            result.user.displayName,
            result.user.photoURL
          );
        }
      })
      .catch((error) => {
        console.error('Redirect result error:', error);
      });

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        await loadProfile(firebaseUser.uid);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  async function signInWithGoogle() {
    // Use redirect instead of popup to avoid COOP issues
    await signInWithRedirect(auth, googleProvider);
  }

  async function signInWithEmail(email: string, password: string) {
    await signInWithEmailAndPassword(auth, email, password);
  }

  async function signUpWithEmail(email: string, password: string) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(result.user);
    if (isSupabaseConfigured()) {
      const newProfile = await createProfile(result.user.uid, {});
      setProfile(newProfile);
    }
  }

  async function resetPassword(email: string) {
    await sendPasswordResetEmail(auth, email);
  }

  async function logout() {
    await signOut(auth);
    setProfile(null);
    setGmailAccessToken(null);
    sessionStorage.removeItem('gmail_access_token');
  }

  async function refreshProfile() {
    if (user) {
      await loadProfile(user.uid);
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        gmailAccessToken,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        resetPassword,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
